// Enable Next.js client-side rendering for this component
'use client'

// Import cn utility for conditional class names
import { cn } from '@/utilities/ui'
import { CMSLink } from '@/components/Link'
import { useEffect, useRef, useState } from 'react'

// Main block component for displaying partner marquee
const PratnerBlocks = (props: any) => {
  const { pratners } = props

  // Base list from props
  const base = Array.isArray(pratners) ? pratners : []
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const [repeatCount, setRepeatCount] = useState(2)

  // Calculate repeats so a single group always spans at least the wrapper width (plus one extra)
  useEffect(() => {
    if (!wrapperRef.current || !measureRef.current) return
    let raf = 0
    const measure = () => {
      const wrapperW = wrapperRef.current!.clientWidth
      const baseW = measureRef.current!.scrollWidth || 1
      // Repeat enough times to fill wrapper and add 1 extra copy to avoid any seam
      const needed = Math.max(2, Math.ceil(wrapperW / baseW) + 1)
      setRepeatCount(needed)
    }
    const onResize = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }
    raf = requestAnimationFrame(measure)
    window.addEventListener('resize', onResize)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
    }
  }, [base.length])

  // Build one repeated sequence (S) that covers the viewport
  const repeatedOnce = Array.from({ length: repeatCount }).flatMap((_, i) =>
    base.map((pratner: any, index: number) => ({ pratner, key: `${index}-${i}` })),
  )
  // Duplicate S inside the same group (S + S) so the 50% animation seam is seamless
  const renderItems = [...repeatedOnce, ...repeatedOnce]

  return (
    <section
      className={cn('w-full', 'bg-primary', 'text-primary-foreground')}
      aria-label="Partners Marquee"
    >
      <div className="container mx-auto py-10">
        <div className="relative overflow-hidden" ref={wrapperRef}>
          {/* Hidden measurement group with a single base pass to determine width */}
          <div className="marquee__group marquee__measure" ref={measureRef} aria-hidden>
            {base.map((pratner: any, index: number) => {
              const { name } = (pratner as any) || {}
              const key = String(name ?? 'partner') + '-' + index + '-measure'
              return (
                <div key={key} className="marquee__item">
                  <span className="sliding-text__title text-4xl font-bold whitespace-nowrap">
                    {name}
                  </span>
                </div>
              )
            })}
          </div>
          {/* Track that scrolls continuously; single group contains duplicated sequence for seamless looping */}
          <div className="marquee__track" aria-hidden={base.length === 0}>
            <div className="marquee__group">
              {renderItems.map(({ pratner, key }) => {
                const { name, link } = (pratner as any) || {}
                const aKey = String(name ?? 'partner') + '-' + key + '-a'

                // Normalize link into CMSLink props like ListBlock
                const linkProps = (() => {
                  if (!link) return null
                  if (typeof link === 'string') return { type: 'custom', url: link }
                  if (
                    link &&
                    typeof link === 'object' &&
                    ('type' in link || 'url' in link || 'reference' in link)
                  )
                    return link as any
                  return null
                })()

                const content = (
                  <span className="sliding-text__title text-4xl font-bold whitespace-nowrap">
                    {name}
                  </span>
                )

                return (
                  <div key={aKey} className="marquee__item">
                    {linkProps ? (
                      <CMSLink
                        {...(linkProps as any)}
                        appearance="inline"
                        className="no-underline"
                        renderLabelStrategy="never"
                        aria-label={String(name ?? 'partner')}
                      >
                        {content}
                      </CMSLink>
                    ) : (
                      content
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Local styles for continuous marquee animation */}
      <style jsx>{`
        .marquee__track {
          display: flex;
          width: max-content;
          will-change: transform;
          animation: marquee-scroll 45s linear infinite;
        }
        .marquee__group {
          display: inline-flex;
          align-items: center;
          gap: 2.5rem; /* gap between partners */
          /* no extra padding to avoid visible seam */
          flex-shrink: 0; /* prevent group from shrinking below needed width */
        }
        .marquee__measure {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .marquee__item {
          flex: 0 0 auto;
        }
        @keyframes marquee-scroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        /* Respect reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .marquee__track {
            animation-duration: 0s;
          }
        }
      `}</style>
    </section>
  )
}

// Export the block component
export default PratnerBlocks
